<template>
    <div>
        <a-row type="flex" justify="end" class="mb-15">
            <!-- ------------------
                COLUMN FILTER
            ------------------- -->
            <a-dropdown v-model="visibleDropdown">
                <a-button icon="eye">{{ lang.users.columns }}</a-button>
                <a-menu slot="overlay">
                    <a-menu-item
                        v-for="(field, key) in fields.filter(f => f.dataIndex !== 'url')"
                        v-show="field.dataIndex !== 'id'"
                        :key="key">
                        <a-checkbox v-model="columns[key]" :value="field.dataIndex">
                            {{ field.title }} <a-icon v-show="!columns[key]" type="eye-invisible" />
                        </a-checkbox>
                    </a-menu-item>
                </a-menu>
            </a-dropdown>
        </a-row>

        <!-- ------------------
              TABLE START
        ------------------- -->
        <a-table
            size="middle"
            :columns="fields.filter((field, key) => columns[key])"
            :dataSource="paginated.data"
            :loading="isLoading"
            :pagination="paginated"
            @change="fetchUsers({ page: $event.current, force: true })"
            bordered :row-key="(record, index) => index">

            <!-- ------------------------------
                TABLE HEADER WITH BUTTONS
            ------------------------------- -->
            <template #title>
                <a-row type="flex" align="middle" justify="space-between">
                    <a-col>
                        <h2 class="mb-0">{{ lang.users.main_table_title }}</h2>
                    </a-col>
                    <a-col>
                        <a-row type="flex" :gutter="8">
                            <a-col class="my-sm-2 w-sm-100p">
                                <a-button @click="toggleModal('create_user')">
                                    {{ lang.users.add_user_button }}
                                </a-button>
                            </a-col>
                            <a-col class="my-sm-2 w-sm-100p">
                                    <a-tooltip placement="top">
                                        <template slot="title">
                                            <span>
                                                Aqui puedes buscar usuarios por ID de cliente, nombre o correo
                                            </span>
                                        </template>
                                        <a-input-search
                                            placeholder="Buscar"
                                            @search="fetchUsers({ force: true, page: 1, search: $event })"
                                            enter-button/>
                                    </a-tooltip>
                                </a-col>
                            <a-col class="my-sm-2 w-sm-100p">
                                <a-button block @click="fetchUsers({ force: true })">
                                    {{ lang.users.refresh }}
                                </a-button>
                            </a-col>
                        </a-row>
                    </a-col>
                </a-row>
            </template>

            <template #username="item">
                {{ item || '-' }}
            </template>

            <!-- ----------------------
                  SEE USER BUTTON
            ----------------------- -->
            <template #id="id">
                <a-button type="link" @click="setUser(id)">
                    {{ lang.users.see_user }}
                </a-button>
            </template>

            <!-- ----------------------
                  TRANSLATED TYPE
            ----------------------- -->
            <template #type="type">
                {{ lang.users.role_options[type] }}
            </template>

            <template #created_at="date">
                {{ new Date(date).toLocaleDateString() }}
            </template>

            <!-- ----------------------
                  DELETE BUTTON
            ----------------------- -->
            <template #delete="id">
                <a-popconfirm
                    v-if="id !== logged.id"
                    :title="lang.users.remove_user_popconfirm.title"
                    :ok-text="lang.users.remove_user_popconfirm.ok_button"
                    :cancel-text="lang.users.remove_user_popconfirm.cancel_button"
                    @confirm="removeUser(id)">
                    <a-button type="danger" icon="close" shape="circle"/>
                </a-popconfirm>
            </template>
        </a-table>

        <!-- ----------------------
            CREATE|UPDATE MODAL
        ----------------------- -->
        <a-modal :title="lang.users[type]" :visible="visible" :footer="null" mask-closable @cancel="toggleModal(type)">
           <a-form class="login-form" @submit.prevent="type === 'create_user' ? register(user) : update(user) ">
                <a-row type="flex" :gutter="8" justify="space-between">

                   <a-col style="flex-grow: 1;">
                        <a-form-item
                            :label="lang.users.add_user_modal.name_label"
                            :validate-status="hasError('name') ? 'error' : ''"
                            :has-feedback="true"
                            :help="getTranslatedError('name')">
                            <a-input
                                id="name"
                                v-model="user.name"
                                :placeholder="lang.users.add_user_modal.name_placeholder"
                                @keyup="removeError('name')">
                                <a-icon slot="prefix" type="user" style="color: rgba(0,0,0,.25)" />
                            </a-input>
                        </a-form-item>
                   </a-col>

                   <a-col style="flex-grow: 1;">
                        <a-form-item
                            :label="lang.users.add_user_modal.role_label"
                            :validate-status="hasError('type') ? 'error' : ''"
                            :has-feedback="true"
                            :help="getTranslatedError('type')">
                            <a-select
                                :disabled="user.id === logged.id"
                                :value="lang.users.role_options[user.type]"
                                @change="user.type = $event"
                                style="width: 100%"
                                allow-clear>
                                    <a-select-option
                                        v-for="(value, attribute, index) in lang.users.role_options"
                                        :key="index"
                                        :value="attribute">
                                            {{ value }}
                                    </a-select-option>
                            </a-select>
                        </a-form-item>
                   </a-col>
                </a-row>

                <a-form-item
                    label="ID de cliente"
                    :validate-status="hasError('username') ? 'error' : ''"
                    :help="getTranslatedError('username')"
                    :has-feedback="true">
                        <a-input
                            id="username"
                            v-model="user.username"
                            :placeholder="`ID DE CLIENTE`"
                            @keyup="removeError('username')">
                        </a-input>
                </a-form-item>

                 <a-form-item
                     label="Correo electrónico del cliente"
                     :validate-status="hasError('email') ? 'error' : ''"
                     :help="getTranslatedError('email')"
                     :has-feedback="true">
                   <a-input
                       id="email"
                       type="email"
                       v-model="user.email"
                       placeholder="mail@example.es"
                       @keyup="removeError('email')">
                   </a-input>
                 </a-form-item>

                 <a-form-item
                   label="Contraseña"
                   :validate-status="hasError('password') ? 'error' : ''"
                   :help="getTranslatedError('password')"
                   :has-feedback="true">
                   <a-input
                     id="password"
                     type="password"
                     v-model="user.password"
                     placeholder="**********"
                     @keyup="removeError('password')">
                   </a-input>
                 </a-form-item>

                <!-- <a-form-item
                    v-if="user.hasOwnProperty('email')"
                    :label="lang.users.add_user_modal.email_label"
                    :validate-status="hasError('email') ? 'error' : ''"
                    :help="getTranslatedError('email')"
                    :has-feedback="true">
                        <a-input
                            id="email"
                            v-model="user.email"
                            :placeholder="lang.users.add_user_modal.email_placeholder"
                            @keyup="removeError('email')">
                                <a-icon slot="prefix" type="user" style="color: rgba(0,0,0,.25)" />
                        </a-input>d
                </a-form-item> -->

                <a-form-item>
                    <a-button :loading="isLoading" type="primary" html-type="submit" class="login-form-button" block>
                        {{ lang.users.confirm }}
                    </a-button>
                </a-form-item>
            </a-form>
        </a-modal>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex';
export default {
    data: () => ({
        user: { name: '', type: 2, username: '', password: '' },
        type: '',
        columns: [],
        visibleDropdown: false,
        search: ''
    }),
    computed: {
        ...mapState({
            paginated: state => state.user.paginated,
            visible: state => state.user.visible_create,
            isLoading: state => state.user.isLoading,
            logged: state => state.session.user
        }),
        ...mapGetters({
            lang: 'lang',
            getTranslatedError: 'getTranslatedError',
            fields: 'user/fields',
            hasError: 'hasError',
        })
    },
    mounted() {
        this.fetchUsers({ force: false, page: this.$route.query.page });
        this.columns = this.fields.filter(f => f.dataIndex !== 'url').map(() => true)
    },
    methods: {
        ...mapActions({
            fetchUsers: 'user/fetchUsers',
            register: 'user/register',
            update: 'user/update',
            removeUser: 'user/remove',
            removeError: 'removeError'
        }),
        ...mapMutations({
            toggleVisibleModal: 'user/TOGGLE_VISIBLE_MODAL'
        }),

        /**
         *
         * @param {int} id
         */
        setUser(id) {
            let user = this.paginated.data.find(user => user.id === id);
            if (user) {
                this.toggleModal();
                // delete u.email;
                this.user = {...user}
            }
        },

        /**
         *
         * @param {string} type
         */
        toggleModal(type = 'update_user') {
            this.type = type;
            this.user = { name: '', email: '', role: 2 };
            this.toggleVisibleModal()
        }
    },
    filters: {
        /**
         *
         * @param {string} val
         * @param {object} user
         * @returns {*}
         */
        withUserName(val, user) {
            return val.replace("#user#", user.name)
        }
    }
}
</script>
